@if (isLoading && !currentDocument) {
  <section class="website-preview-page__loading">
    <ion-spinner name="crescent"></ion-spinner>
    <p class="mb-0">Loading full website preview...</p>
  </section>
} @else if (loadError && !currentDocument) {
  <div class="website-preview-page__error-shell">
    <app-api-load-error
      title="Preview unavailable"
      [message]="loadError"
      retryLabel="Retry"
      (retry)="retryLoad()"
    ></app-api-load-error>
  </div>
} @else {
  <div class="website-preview-page">
    <header class="website-preview-page__toolbar">
      <div class="website-preview-page__toolbar-main">
        <div class="website-preview-page__toolbar-actions">
          <a class="website-preview-page__ghost-button" routerLink="/websites">
            <ion-icon name="chevron-back-outline"></ion-icon>
            <span>Back to websites</span>
          </a>
          @if (website && currentPage) {
            <button type="button" class="website-preview-page__ghost-button" (click)="openCurrentPageBuilder()">
              <ion-icon name="create-outline"></ion-icon>
              <span>Edit current page</span>
            </button>
          }
        </div>

        <div class="website-preview-page__identity">
          <h1 class="website-preview-page__title mb-0 bw-title-with-info">
            {{ website?.name || 'Website preview' }}
            <app-info-tooltip
              compact
              placement="right"
              [text]="'Viewing ' + (currentPage?.name || 'the draft page') + ' at ' + currentPathLabel + (revisionPreviewLabel ? ' (' + revisionPreviewLabel + ')' : '') + '. Use this view to inspect the current draft without the editor shell; internal page controls follow the same draft website flow.'"
            ></app-info-tooltip>
          </h1>
        </div>
      </div>

      <div class="website-preview-page__toolbar-controls">
        <label class="website-preview-page__field">
          <span class="website-preview-page__field-label">
            Page
            <app-info-tooltip compact placement="bottom" text="Switch pages directly from the preview if you want to jump around the website without relying only on internal navigation buttons."></app-info-tooltip>
          </span>
          <select
            class="website-preview-page__select"
            [ngModel]="currentPage?.publicId || null"
            (ngModelChange)="navigateToPage($event)"
          >
            @for (page of pages; track trackPage($index, page)) {
              <option [ngValue]="page.publicId">{{ page.name }} - {{ page.path }}</option>
            }
          </select>
        </label>

        <label class="website-preview-page__field">
          <span class="website-preview-page__field-label">
            Theme
            <app-info-tooltip compact placement="bottom" text="Preview the same draft website with different canvas-theme packages without changing the rest of the app shell."></app-info-tooltip>
          </span>
          <select
            class="website-preview-page__select"
            [ngModel]="currentCanvasThemePackageId"
            (ngModelChange)="selectCanvasThemePackage($event)"
          >
            @for (themePackage of canvasThemeService.packages(); track themePackage.id) {
              <option [value]="themePackage.id">{{ themePackage.name }}</option>
            }
          </select>
        </label>

        <div class="website-preview-page__mode-group">
          <span class="website-preview-page__field-label">Canvas mode</span>
          <div class="website-preview-page__mode-switch">
            <button
              type="button"
              class="website-preview-page__mode-button"
              [class.is-active]="currentCanvasThemeMode === 'light'"
              aria-label="Canvas light mode"
              title="Canvas light mode"
              (click)="setCanvasThemeMode('light')"
            >
              <ion-icon name="sunny-outline"></ion-icon>
            </button>
            <button
              type="button"
              class="website-preview-page__mode-button"
              [class.is-active]="currentCanvasThemeMode === 'dark'"
              aria-label="Canvas dark mode"
              title="Canvas dark mode"
              (click)="setCanvasThemeMode('dark')"
            >
              <ion-icon name="moon-outline"></ion-icon>
            </button>
          </div>
        </div>

        <div class="website-preview-page__mode-group">
          <span class="website-preview-page__field-label">
            Viewport
            <app-info-tooltip compact placement="bottom" text="Switch between desktop, tablet, and mobile widths while keeping the website preview in a normal full-page flow."></app-info-tooltip>
          </span>
          <div class="website-preview-page__mode-switch">
            @for (preset of previewPresets; track preset.id) {
              <button
                type="button"
                class="website-preview-page__mode-button"
                [class.is-active]="previewMode === preset.id"
                [attr.aria-label]="preset.label"
                [title]="preset.label"
                (click)="setPreviewMode(preset.id)"
              >
                <ion-icon [name]="preset.icon"></ion-icon>
              </button>
            }
          </div>
        </div>

        <div class="website-preview-page__meta-strip">
          <span class="website-preview-page__meta-chip">{{ currentCanvasThemeMode | titlecase }}</span>
          <span class="website-preview-page__meta-chip">{{ canvasWidth }} x {{ canvasHeight }}</span>
        </div>
      </div>
    </header>

    @if (pageError) {
      <div class="website-preview-page__alert">{{ pageError }}</div>
    }

    <main class="website-preview-page__stage" [ngStyle]="canvasShellStyle">
      <div class="website-preview-page__stage-inner">
        @if (isPageLoading) {
          <div class="website-preview-page__loading website-preview-page__loading--inline">
            <ion-spinner name="crescent"></ion-spinner>
            <p class="mb-0">Loading page preview...</p>
          </div>
        }

        @if (currentDocument && currentPage) {
          <div class="website-preview-page__viewport" [ngStyle]="canvasViewportStyle">
            <div class="website-preview-page__root" [ngStyle]="nodeStyles(currentDocument.root)">
              @if (!currentDocument.root.children.length) {
                <div class="website-preview-page__empty">
                  This page draft does not have any visible sections yet.
                </div>
              }

              @for (child of currentDocument.root.children; track trackNode($index, child)) {
                <ng-container [ngTemplateOutlet]="previewNode" [ngTemplateOutletContext]="{ $implicit: child }"></ng-container>
              }
            </div>
          </div>
        }
      </div>
    </main>
  </div>

  <ng-template #previewNode let-node>
    <div
      class="website-preview-page__node"
      [ngClass]="previewNodeClasses(node)"
      [ngStyle]="nodeStyles(node)"
      (click)="handlePreviewNodeClick(node, $event)"
    >
      @switch (node.type) {
        @case ('text') {
          @if (node.content.eyebrow) {
            <p class="bw-node-eyebrow">{{ node.content.eyebrow }}</p>
          }
          @if (node.content.title) {
            <h2 class="bw-node-title">{{ node.content.title }}</h2>
          }
          @if (node.content.text) {
            <p class="bw-node-copy">{{ node.content.text }}</p>
          }
          @if (node.content.secondaryText) {
            <p class="bw-node-copy bw-node-copy--secondary">{{ node.content.secondaryText }}</p>
          }
        }
        @case ('button') {
          <button type="button" class="bw-button-node" (click)="handlePreviewButtonClick(node)">
            {{ node.content.buttonText || node.content.title || 'Button' }}
          </button>
        }
        @case ('media') {
          <div class="bw-media-node" [class.bw-media-node--with-image]="node.content.mediaUrl">
            @if (node.content.mediaUrl) {
              <img class="bw-media-image" [src]="resolveMediaUrl(node.content.mediaUrl)" [alt]="node.content.mediaAlt || ''" decoding="async" />
            }
            @if (node.content.mediaLabel || node.content.caption) {
              <div class="bw-media-content">
                @if (node.content.mediaLabel) {
                  <span class="bw-media-label">{{ node.content.mediaLabel }}</span>
                }
                @if (node.content.caption) {
                  <span class="bw-media-caption">{{ node.content.caption }}</span>
                }
              </div>
            }
          </div>
        }
        @case ('commerce') {
          <div class="website-preview-page__commerce-node">
            <div class="website-preview-page__commerce-toolbar">
              <span>Commerce runtime</span>
              <span>{{ node.content.commerceWidget || 'storefront' }}</span>
            </div>
            <div class="website-preview-page__commerce-grid">
              <aside>
                <strong>Filtre catalog</strong>
                <span>Categorii</span>
                <span>Brand</span>
                <span>Disponibilitate</span>
              </aside>
              <div>
                @for (item of [1, 2, 3, 4, 5, 6]; track item) {
                  <article>
                    <div></div>
                    <strong>Produs demo {{ item }}</strong>
                    <span>Descriere scurtă, rating, preț și stoc.</span>
                    <b>€{{ item * 100 + 99 }}</b>
                  </article>
                }
              </div>
            </div>
          </div>
        }
        @case ('badge') {
          <span class="bw-badge-node">{{ node.content.badgeText || node.content.title || 'Badge' }}</span>
        }
      }

      @if (isContainer(node) && node.children.length) {
        @for (child of node.children; track trackNode($index, child)) {
          <ng-container [ngTemplateOutlet]="previewNode" [ngTemplateOutletContext]="{ $implicit: child }"></ng-container>
        }
      }
    </div>
  </ng-template>
}
